import { useState } from 'react';
import SEO from '../../components/SEO';
import { leadService } from '../../lib/supabase';

export default function FreeValuationPage() {
  const [showContactModal, setShowContactModal] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [showFAQ, setShowFAQ] = useState<number | null>(null);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://buyinzagreb.com';

  const valuationPageSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Besplatna procjena nekretnine Zagreb",
    "description": "Stručna procjena tržišne vrijednosti, analiza rizika i savjetovanje o strategiji prodaje nekretnina u Zagrebu",
    "provider": {
      "@type": "RealEstateAgent",
      "name": "BuyInZagreb",
      "telephone": "+385953845657",
      "email": "info@buyinzagreb.com"
    },
    "areaServed": {
      "@type": "City",
      "name": "Zagreb"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const leadData = {
        name: formData.get('name') as string,
        email: formData.get('email') as string || '',
        phone: formData.get('phone') as string || '',
        message: `Adresa nekretnine: ${formData.get('address') || 'N/A'}, Tip nekretnine: ${formData.get('property_type') || 'N/A'}, Površina: ${formData.get('size') || 'N/A'} m², Godina gradnje: ${formData.get('year') || 'N/A'}`,
        source: 'besplatna procjena nekretnine'
      };

      await leadService.create(leadData);
      
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
    } catch (error) {
      console.error('Error submitting lead:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Besplatna procjena nekretnine Zagreb | BuyInZagreb"
        description="Stručna procjena tržišne vrijednosti nekretnine Zagreb. Analiza rizika, savjetovanje o strategiji prodaje i procjena provizije. Besplatno i bez obveze."
        keywords="procjena nekretnine Zagreb, besplatna procjena, tržišna vrijednost, prodaja nekretnine Zagreb, zastupanje prodavatelja Zagreb"
        canonical={`${siteUrl}/besplatna-procjena-nekretnine-zagreb`}
        schemaData={valuationPageSchema}
      />

      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-2xl border-b border-gray-100/50 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex-shrink-0 cursor-pointer mr-8">
              <img 
                src="https://static.readdy.ai/image/543888419322a681fa4fe0b45060a131/83927b54a7909cd8f2d054a2d7c5ece9.png" 
                alt="BuyInZagreb Logo" 
                className="h-10 w-auto"
              />
            </a>
            
            <nav className="hidden lg:flex items-center space-x-8 flex-1">
              <a href="/" className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 whitespace-nowrap">
                Početna
              </a>
              <a href="/#listings" className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 whitespace-nowrap">
                Nekretnine
              </a>
              <a href="/#services" className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 whitespace-nowrap">
                Usluge
              </a>
              <span className="text-red-600 font-medium whitespace-nowrap">
                Besplatna procjena
              </span>
              <a href="/#about" className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 whitespace-nowrap">
                O nama
              </a>
              <a href="/#blog-tools" className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 whitespace-nowrap">
                Blog
              </a>
              <a href="/#contact" className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 whitespace-nowrap">
                Kontakt
              </a>
            </nav>

            <div className="flex items-center space-x-4 flex-shrink-0">
              <button 
                onClick={() => setShowContactModal(true)}
                className="bg-red-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-red-700 hover:shadow-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Zatražite procjenu
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-16">
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          {/* AI Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-red-500/8 via-white to-blue-500/8"></div>
          <div className="absolute top-0 right-0 w-2/3 h-2/3 bg-gradient-to-br from-red-400/12 via-transparent to-blue-400/8 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-2/3 h-2/3 bg-gradient-to-tr from-blue-400/10 via-transparent to-red-400/8 blur-3xl"></div>
          
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-8 leading-tight tracking-tight">
                Besplatna procjena<br />
                <span className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 bg-clip-text text-transparent">
                  vrijednosti
                </span><br />
                nekretnine
              </h1>
              
              <p className="text-xl lg:text-2xl text-gray-600 mb-16 leading-relaxed max-w-3xl mx-auto font-light">
                Stručna procjena tržišne vrijednosti, analiza rizika i savjetovanje o strategiji prodaje
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <button 
                  onClick={() => setShowContactModal(true)}
                  className="group bg-red-600 text-white px-10 py-5 rounded-2xl text-xl font-semibold hover:bg-red-700 hover:shadow-2xl hover:shadow-red-500/30 transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105"
                >
                  <i className="ri-calculator-line mr-3 group-hover:scale-110 transition-transform"></i>
                  Zatražite besplatnu procjenu
                </button>
                <button 
                  onClick={() => document.getElementById('how-we-value')?.scrollIntoView({ behavior: 'smooth' })}
                  className="group bg-white/60 backdrop-blur-2xl border border-white/80 text-gray-700 px-10 py-5 rounded-2xl text-xl font-semibold hover:bg-white/80 hover:shadow-2xl transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105"
                >
                  Saznajte više
                  <i className="ri-arrow-down-line ml-3 group-hover:translate-y-1 transition-transform"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* How We Value Section */}
        <section id="how-we-value" className="py-20 bg-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-red-500/3 via-transparent to-blue-500/3"></div>
          
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                Kako procjenjujemo vrijednost
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
                Naša procjena temelji se na detaljnoj analizi nekretnine i tržišnih trendova
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <img 
                  src="https://public.readdy.ai/ai/img_res/db20c96bc32a1a28c4083f6d0bf0c7b4.jpg"
                  alt="Proces procjene nekretnine"
                  className="rounded-2xl shadow-xl"
                />
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Obilazak nekretnine</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    Naš agent dolazi na teren i detaljno pregledava nekretninu. Mjerimo sve prostorije, fotografiramo stanje, bilježimo sve relevantne karakteristike koje utječu na vrijednost.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <h4 className="text-xl font-bold text-gray-900">Što analiziramo:</h4>
                  <ul className="space-y-2">
                    {[
                      'Lokacija - kvart, blizina centra, infrastruktura, prometna povezanost',
                      'Stanje nekretnine - kvaliteta gradnje, potreba za adaptacijom, instalacije',
                      'Etažiranost - kat, orijentacija, pogled, lift, parking',
                      'Tržišni trendovi - recentne prodaje u kvartu, potražnja, sezonalnost',
                      'Posebne karakteristike - terasa, balkon, garaža, spremište, vrt'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start">
                        <i className="ri-check-line text-green-600 mr-3 text-lg mt-1 flex-shrink-0"></i>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-red-50/30 rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Usporedba s tržištem</h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Nakon obilaska, uspoređujemo vašu nekretninu s recentnim prodajama sličnih nekretnina u istom kvartu. Analiziramo:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    icon: 'ri-line-chart-line',
                    title: 'Prodajne cijene',
                    description: 'Realne cijene po kojima su se prodavale slične nekretnine u zadnjih nekoliko mjeseci'
                  },
                  {
                    icon: 'ri-time-line',
                    title: 'Vrijeme prodaje',
                    description: 'Koliko dugo su nekretnine bile na tržištu prije prodaje'
                  },
                  {
                    icon: 'ri-percent-line',
                    title: 'Sniženja cijena',
                    description: 'Za koliko su prodavatelji morali sniziti početnu cijenu'
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-white rounded-xl p-6 shadow-md">
                    <div className="w-12 h-12 flex items-center justify-center bg-red-600 rounded-xl mb-4">
                      <i className={`${item.icon} text-white text-xl`}></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h4>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Risk Assessment Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-red-50/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-br from-red-400/10 to-transparent blur-3xl"></div>
          
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                Procjena rizika
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
                Identificiramo sve potencijalne rizike koji mogu utjecati na prodaju
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: 'Pravni rizici',
                  items: [
                    'Provjera vlasništva i suvlasnika',
                    'Analiza teretovnice (hipoteke, zaloge)',
                    'Kontrola plombi i zabrana otuđenja',
                    'Provjera dugovanja prema komunalnim službama',
                    'Analiza građevinskih i uporabnih dozvola'
                  ]
                },
                {
                  icon: 'ri-line-chart-line',
                  title: 'Tržišni rizici',
                  items: [
                    'Likvidnost - koliko brzo se prodaju nekretnine u kvartu',
                    'Potražnja - ima li dovoljno kupaca za tu vrstu nekretnine',
                    'Realna očekivanja cijene - je li vaša cijena tržišno prihvatljiva',
                    'Konkurencija - koliko sličnih nekretnina je trenutno na prodaju',
                    'Sezonalnost - je li sada pravo vrijeme za prodaju'
                  ]
                },
                {
                  icon: 'ri-calendar-line',
                  title: 'Vremenski rizici',
                  items: [
                    'Procjena realnog vremena prodaje',
                    'Identifikacija faktora koji mogu usporiti prodaju',
                    'Plan aktivnosti za ubrzanje procesa',
                    'Analiza potrebe za adaptacijom prije prodaje',
                    'Strategija pozicioniranja na tržištu'
                  ]
                }
              ].map((risk, index) => (
                <div key={index} className="bg-white border border-gray-100 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-red-600 to-red-500 rounded-2xl mb-6 shadow-lg shadow-red-500/25">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 backdrop-blur-xl rounded-xl">
                      <i className={`${risk.icon} text-white text-xl`}></i>
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {risk.title}
                  </h3>
                  
                  <ul className="space-y-2">
                    {risk.items.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <i className="ri-check-line text-green-600 mr-2 text-lg mt-0.5 flex-shrink-0"></i>
                        <span className="text-gray-600 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Commission Structure Section */}
        <section className="py-20 bg-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/3 via-transparent to-red-500/3"></div>
          
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                Visina provizije
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed font-light">
                Fer model naplate - provizija u skladu s rizikom i poslom koji radimo
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-red-50/30 rounded-2xl p-8 shadow-xl border border-gray-100">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Visina naše provizije nije fiksna - određuje se individualno za svaku nekretninu na temelju kompleksnosti slučaja, razine rizika i dodatnih usluga koje pružamo.
              </p>

              <div className="space-y-4 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-settings-3-line text-red-600 mr-3 text-xl"></i>
                    Kompleksnost slučaja
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Jednostavna prodaja stana s urednom dokumentacijom zahtijeva manje posla od prodaje kuće s više suvlasnika, neriješenim imovinsko-pravnim odnosima ili potrebom za legalizacijom.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-alert-line text-red-600 mr-3 text-xl"></i>
                    Razina rizika
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Nekretnine s pravnim problemima, dugovima ili u lošem stanju nose veći rizik i zahtijevaju više rada na rješavanju problema prije prodaje. Ovo se odražava na visinu provizije.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-service-line text-red-600 mr-3 text-xl"></i>
                    Dodatne usluge
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Ako uz prodaju pružamo i dodatne usluge - pravnu podršku, kreditno savjetovanje, koordinaciju adaptacije, energetsko certificiranje - to se uračunava u ukupnu proviziju.
                  </p>
                </div>
              </div>

              <div className="bg-red-600 text-white rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold mb-3">Naš pristup</h3>
                <p className="text-lg leading-relaxed opacity-95">
                  Provizija se naplaćuje tek nakon uspješne prodaje. Ako ne prodamo vašu nekretninu, ne plaćate ništa. Točnu visinu provizije dogovaramo nakon besplatne procjene i analize rizika.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Consulting Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-red-50/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-br from-red-400/10 to-transparent blur-3xl"></div>
          
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                Savjetovanje o strategiji prodaje
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
                Pomažemo vam donijeti prave odluke za uspješnu prodaju
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {[
                {
                  icon: 'ri-price-tag-3-line',
                  title: 'Preporuka početne cijene',
                  description: 'Na temelju tržišne analize predlažemo optimalnu početnu cijenu - dovoljno visoku da ostavlja prostor za pregovaranje, ali ne toliko visoku da odbija kupce. Objašnjavamo strategiju pozicioniranja i očekivane scenarije pregovaranja.'
                },
                {
                  icon: 'ri-calendar-check-line',
                  title: 'Optimalno vrijeme prodaje',
                  description: 'Savjetujemo vas kada je najbolje vrijeme za izlazak na tržište. Tržište nekretnina ima sezonalnost - proljeće i jesen su najaktivniji periodi. Ako imate fleksibilnost, možemo pričekati povoljniji trenutak.'
                },
                {
                  icon: 'ri-paint-brush-line',
                  title: 'Priprema nekretnine',
                  description: 'Dajemo konkretne preporuke što popraviti, obnoviti ili promijeniti prije izlaska na tržište. Ponekad manje ulaganje u kozmetičke popravke može značajno povećati prodajnu cijenu.'
                },
                {
                  icon: 'ri-megaphone-line',
                  title: 'Strategija marketinga',
                  description: 'Planiramo kako ćemo oglašavati nekretninu - na kojim portalima, s kojim fotografijama, kojim tekstom. Objašnjavamo kako privući prave kupce i izbjeći one koji samo "razgledavaju" bez ozbiljne namjere kupnje.'
                }
              ].map((item, index) => (
                <div key={index} className="bg-white border border-gray-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-600 rounded-xl mb-4">
                    <i className={`${item.icon} text-white text-lg`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <div className="bg-gradient-to-br from-gray-50 to-red-50/30 rounded-2xl p-8 max-w-3xl mx-auto border border-gray-100 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Spremni za besplatnu procjenu?</h3>
                <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto leading-relaxed">
                  Kontaktirajte nas danas i saznajte koliko vrijedi vaša nekretnina. Procjena je potpuno besplatna i bez obveze.
                </p>
                <button 
                  onClick={() => setShowContactModal(true)}
                  className="bg-red-600 text-white px-10 py-4 rounded-xl text-xl font-semibold hover:bg-red-700 hover:shadow-xl hover:shadow-red-500/25 transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105"
                >
                  <i className="ri-calculator-line mr-3"></i>
                  Zatražite besplatnu procjenu
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-2xl z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-3xl rounded-2xl p-8 max-w-xl w-full shadow-2xl border border-white/20 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Zatražite besplatnu procjenu</h3>
              <button 
                onClick={() => setShowContactModal(false)}
                className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-100/80 rounded-xl transition-all duration-200 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <p className="text-gray-600 mb-6 leading-relaxed">
              Popunite formu i kontaktirat ćemo vas u roku od 24 sata za dogovor o terminu obilaska.
            </p>
            
            <form id="valuation-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Ime i prezime *</label>
                <input 
                  type="text"
                  name="name"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                  placeholder="Vaše ime i prezime"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Email *</label>
                <input 
                  type="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                  placeholder="vas.email@primjer.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Kontakt telefon *</label>
                <input 
                  type="tel"
                  name="phone"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                  placeholder="+385 xx xxx xxxx"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Adresa nekretnine *</label>
                <input 
                  type="text"
                  name="address"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                  placeholder="Ulica i broj, Zagreb"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Tip nekretnine *</label>
                <select 
                  name="property_type"
                  required
                  className="w-full px-4 py-3 pr-8 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                >
                  <option value="">Odaberite tip</option>
                  <option value="stan">Stan</option>
                  <option value="kuca">Kuća</option>
                  <option value="poslovni-prostor">Poslovni prostor</option>
                  <option value="zemljiste">Zemljište</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Površina (m²) *</label>
                  <input 
                    type="number"
                    name="size"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                    placeholder="npr. 65"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Godina gradnje</label>
                  <input 
                    type="number"
                    name="year"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                    placeholder="npr. 2010"
                  />
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <input 
                  type="checkbox"
                  id="gdpr-consent"
                  name="gdpr_consent"
                  required
                  className="mt-1 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <label htmlFor="gdpr-consent" className="text-sm text-gray-600 leading-relaxed">
                  Suglasan/na sam da se moji podaci koriste isključivo za procjenu nekretnine i kontakt.
                </label>
              </div>
              
              <button 
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full bg-red-600 text-white py-4 rounded-xl text-lg font-medium hover:bg-red-700 disabled:bg-gray-400 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                {formStatus === 'submitting' ? 'Šalje se...' : formStatus === 'success' ? 'Vaš zahtjev je zaprimljen!' : formStatus === 'error' ? 'Greška - pokušajte ponovo' : 'Zatražite besplatnu procjenu'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Footer - Black design */}
      <footer className="bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-800/50 via-transparent to-black/50"></div>
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Company info */}
            <div className="space-y-4">
              <h3 className="text-2xl font-bold">BuyInZagreb</h3>
              <p className="text-gray-300 leading-relaxed">
                Vaš partner u bez-rizičnom posredovanju od 2022.
              </p>
              <div className="flex space-x-3">
                {['ri-facebook-fill', 'ri-instagram-line', 'ri-linkedin-fill'].map((icon, index) => (
                  <a key={index} href="#" className="w-10 h-10 flex items-center justify-center bg-white/10 backdrop-blur-xl hover:bg-red-600 rounded-xl transition-all duration-300 cursor-pointer hover:scale-110">
                    <i className={icon}></i>
                  </a>
                ))}
              </div>
            </div>
            
            {/* Quick links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Navigacija</h4>
              <ul className="space-y-2">
                {[
                  { name: 'Početna', href: '/' },
                  { name: 'Nekretnine', href: '/#listings' },
                  { name: 'Usluge', href: '/#services' },
                  { name: 'Besplatna procjena', href: '/seller-representation' },
                  { name: 'Blog', href: '/#blog-tools' }
                ].map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Legal links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Pravni linkovi</h4>
              <ul className="space-y-2">
                {[
                  { name: 'Uvjeti korištenja', href: '/uvjeti-koristenja' },
                  { name: 'Politika privatnosti (GDPR)', href: '/privatnost-gdpr' },
                  { name: 'Politika kolačića', href: '/politika-kolacica' },
                  { name: 'Obrazac za prigovore', href: '/obrazac-za-prigovore' }
                ].map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Company details */}
          <div className="border-t border-gray-700 pt-6">
            <div className="text-center text-gray-400 text-sm space-y-1">
              <p className="font-medium">BUY IN ZAGREB d.o.o.</p>
              <p>Kulmerska ulica 23A, 10000 Zagreb, Hrvatska</p>
              <p>OIB: 17682979076</p>
            </div>
          </div>
          
          {/* Footer bottom */}
          <div className="border-t border-gray-700 pt-6 mt-6 text-center">
            <p className="text-gray-400 text-sm">© 2024 BuyInZagreb. Sva prava pridržana.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
